import React, { useState } from 'react';
import { t } from '@lingui/macro';
import { useCurrentAgreement, useCurrentLocale } from '@economic/react-app';
import { WindowedTable as Table, Spinner, Button } from '@economic/taco';
import { SourceComponent } from '~/types';
import FilterInput from '../../shared/components/FilterInput';
import View from '../../shared/components/View/View';
import { appendSimpleSearchFilter } from '../../../utilities/filters';
import { ProductListView } from '../../../types/internal-api';
import { useInfiniteCollectionQuery } from '../../shared/hooks/useInfiniteCollectionQuery';
import { ViewProps } from '../types';
import { CreateProductDialog } from '../../forms/Inventory/CreateOrEditProduct';
import useLocalStorage from '../../shared/hooks/useLocalStorage';

interface Props extends ViewProps<ProductListView> {
    showPrice?: boolean;
    showCreateOnTheFly?: boolean;
}

export default function ProductsView({
    onCreated: handleCreated,
    onSelected: handleSelected,
    showPrice,
    showCreateOnTheFly: showCreateOnTHeFly,
}: Props) {
    const agreement = useCurrentAgreement();
    const locale = useCurrentLocale();

    const hasInventoryModule = !!agreement.modules.inventory;
    const searchFields = ['productNumber', 'name'];

    const [filter, setFilter] = useState<string>('');
    const { data, loading, sorting, loadMore, length } = useInfiniteCollectionQuery<ProductListView>(
        `/products?filter=${appendSimpleSearchFilter('isAccessible$eq:true', filter, searchFields)}`
    );
    const currencyKey = `${agreement.number}/sales.product.currency`;
    const localeCurrency = locale.currencyCode;

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currency] = useLocalStorage<string>(currencyKey, localeCurrency);

    const emptyStateRenderer = () => {
        if (loading) {
            return <Spinner className="flex justify-center items-center py-8" />;
        }

        return null;
    };
    const showSecondCurrency = !!localeCurrency && !!currency && localeCurrency !== currency;

    return (
        <View>
            <View.Title>{t`Products`}</View.Title>

            <View.Header>
                {showCreateOnTHeFly && (
                    <View.Header.Actions>
                        <div>
                            <Button appearance="primary" onClick={() => setShowCreateModal(true)}>
                                {t`Create product`}
                            </Button>
                        </div>
                    </View.Header.Actions>
                )}
                <View.Header.Filters>
                    <FilterInput filter={filter} onFilter={setFilter} sourceComponent={SourceComponent.ComboBox} />
                </View.Header.Filters>
            </View.Header>

            <View.Content>
                <Table<ProductListView>
                    data={data}
                    onRowClick={row => handleSelected(row.values)}
                    loadMore={loadMore}
                    length={length}
                    emptyStateRenderer={emptyStateRenderer}
                    {...sorting}
                    actions={
                        hasInventoryModule
                            ? [
                                  {
                                      icon: 'inventory-matrix',
                                      text: t`Inventory matrix`,
                                      onClick: row => {
                                          window.top?.PopUp2(
                                              '../lager/varianter_tast.asp?showzero=yes&VareNr=' +
                                                  row.values.productNumber.replace(/\+/g, '%2B'),
                                              750,
                                              500
                                          );
                                      },
                                      tooltip: t`Inventory matrix`,
                                      showOnRow: true,
                                  },
                              ]
                            : undefined
                    }>
                    <Table.Column accessor="productNumber" headRenderer={t`No.`} flex="0 0 150px" sort="asc" />
                    <Table.Column accessor="name" align="left" headRenderer={t`Name`} />
                    {showPrice ? <Table.Column accessor="salesPrice" align="right" /> : null}
                </Table>
                {showCreateModal && (
                    <CreateProductDialog
                        onClose={() => setShowCreateModal(false)}
                        showSaveAndNew={false}
                        onSubmitted={values => handleCreated && handleCreated(values)}
                        showSecondSalesPrice={showSecondCurrency}
                        baseCurrency={currency}
                        defaultOpen
                    />
                )}
            </View.Content>
        </View>
    );
}
